import { useIsSpeaking, useLocalParticipant } from '@livekit/components-react'
import { useEffect, useState } from 'react'

import { t } from './i18n'

/**
 * "You're muted" — for the local participant only.
 *
 * LiveKit keeps reporting speech energy while the local mic track is disabled,
 * so the person talking into a muted mic can be told, from their own device's
 * signal, that nobody can hear them. The roster row and the tile ring already
 * hide speaking-while-muted (`useIsSpeaking(p) && !micMuted`); this is the same
 * signal turned inward: one quiet, dismissible hint. No remote claim — another
 * participant's muted speaking is theirs to notice, not ours to announce — and
 * no audio leaves the device.
 *
 * Dismissal latches until the mic is enabled again: `speaking` re-evaluates on
 * every audio-level event, so a non-latching X would flicker the hint back
 * mid-sentence, which is exactly the nag this replaces.
 */
export function MutedSpeakingHint({ onUnmute }: { onUnmute: () => void }) {
  const { localParticipant } = useLocalParticipant()
  const speaking = useIsSpeaking(localParticipant)
  const micOff = localParticipant ? !localParticipant.isMicrophoneEnabled : false

  const [dismissed, setDismissed] = useState(false)
  // Unmuting IS the resolution, so the latch clears the moment the mic comes
  // on; the hint can re-appear only after muting again.
  useEffect(() => {
    if (!micOff) setDismissed(false)
  }, [micOff])

  if (!micOff || !speaking || dismissed) return null

  return (
    <div className="muted-hint" role="status">
      <p className="muted-hint__text">{t('room.mutedSpeaking')}</p>
      <div className="muted-hint__actions">
        <button type="button" className="pill-button pill-button--accent" onClick={onUnmute}>
          {t('unmute.turnOnMic')}
        </button>
        <button
          type="button"
          className="pill-button pill-button--quiet"
          onClick={() => setDismissed(true)}
        >
          {t('room.dismiss')}
        </button>
      </div>
    </div>
  )
}
